<template>
  <!-- Client Profile View (when reportId is present) -->
  <v-col v-if="showClientProfile">
    <client-profile />
  </v-col>

  <!-- Client List View (default) -->
  <v-col v-else>
    <!-- Compact Header Section with Inline Tabs -->
    <div class="clients-view-header-compact mb-3">
      <v-toolbar
        flat
        color="white"
        class="header-toolbar px-0 py-0"
        height="56"
      >
        <!-- Title Icon -->
        <div class="header-icon-wrapper mr-3">
          <font-awesome-icon
            :icon="['fas', 'building']"
            size="lg"
            class="primary--text"
          />
        </div>

        <!-- Inline Tabs -->
        <v-tabs
          v-model="activeTab"
          color="primary"
          slider-color="primary"
          show-arrows
          class="clients-view-tabs-inline flex-grow-1"
          height="56"
        >
          <v-tab
            v-for="tab in tabs"
            :key="tab.id"
            class="tab-compact"
          >
            <font-awesome-icon
              :icon="tab.icon"
              class="mr-2"
              size="sm"
            />
            {{ tab.label }}
            <v-chip
              v-if="tab.count !== undefined"
              x-small
              outlined
              :color="tab.chipColor || 'brightblue'"
              class="ml-2 tab-count-chip"
            >
              {{ tab.count }}
            </v-chip>
          </v-tab>
        </v-tabs>

        <v-spacer />

        <!-- Action Buttons -->
        <div class="header-actions d-flex align-center">
          <!-- Add Client Button (Admin Only) -->
          <v-btn
            v-if="isUserInternalAdmin"
            color="primary"
            depressed
            class="add-client-btn-compact"
            @click="addClient"
          >
            <font-awesome-icon
              :icon="['fas', 'plus']"
              size="sm"
            />
            <span class="ml-2 d-none d-sm-inline">Add Client</span>
          </v-btn>
        </div>
      </v-toolbar>

      <v-divider />

      <!-- Compact Controls Bar -->
      <div class="controls-bar">
        <v-row class="align-center ma-0 pa-3 pl-2">
          <!-- Search Field -->
          <v-col
            cols="12"
            sm="6"
            md="4"
            lg="3"
            class="py-1"
          >
            <search-field
              v-model="searchText"
              placeholder="Search by client name or billing code..."
            />
          </v-col>

          <!-- Data Tools (Filter, Group, Sort, Columns) -->
          <v-col
            cols="auto"
            class="py-1"
          >
            <tools-toolbar
              :current-required-columns="requiredColumns"
              :report-key="reportKey"
              :table-columns="tableColumns"
              @on-apply-filters="onApplyFilters"
              @on-reset-filters="onResetFilters"
              @on-grouping-change="onGroupingChange"
              @on-sorting-change="onSortingChange"
            >
              <template v-slot:filter-controls>
                <client-filter-content
                  :selected-status-filter="selectedStatusFilter"
                  :selected-timesheet-filter="selectedTimesheetFilter"
                  :selected-config-filter="selectedConfigFilter"
                  :selected-publisher-filter="selectedPublisherFilter"
                  :selected-discrepancy-filter="selectedDiscrepancyFilter"
                  :selected-pacing-filter="selectedPacingFilter"
                  :selected-yield-filter="selectedYieldFilter"
                  :selected-adx-health-filter="selectedAdxHealthFilter"
                  :selected-network-health-filter="selectedNetworkHealthFilter"
                  :status-filter-options="statusFilterOptions"
                  :timesheet-filter-options="timesheetFilterOptions"
                  :config-filter-options="configFilterOptions"
                  :publisher-filter-options="publisherFilterOptions"
                  @update:status-filter="selectedStatusFilter = $event"
                  @update:timesheet-filter="selectedTimesheetFilter = $event"
                  @update:config-filter="selectedConfigFilter = $event"
                  @update:publisher-filter="selectedPublisherFilter = $event"
                  @update:discrepancy-filter="selectedDiscrepancyFilter = $event"
                  @update:pacing-filter="selectedPacingFilter = $event"
                  @update:yield-filter="selectedYieldFilter = $event"
                  @update:adx-health-filter="selectedAdxHealthFilter = $event"
                  @update:network-health-filter="selectedNetworkHealthFilter = $event"
                  @clear-all="clearAllFilters"
                />
              </template>
            </tools-toolbar>
          </v-col>

          <!-- View Mode Dropdown -->
          <v-col
            cols="auto"
            class="py-1"
          >
            <v-menu
              offset-y
              nudge-bottom="6"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  outlined
                  class="text-none"
                  v-bind="attrs"
                  v-on="on"
                >
                  <font-awesome-icon
                    :icon="viewMode === 'grid' ? ['fas', 'grip'] : ['fas', 'table']"
                    size="sm"
                    class="mr-2"
                  />
                  {{ viewMode === 'grid' ? 'Grid' : 'Table' }}
                  <font-awesome-icon
                    :icon="['fas', 'chevron-down']"
                    size="xs"
                    class="ml-2"
                  />
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item
                  :class="{ 'v-list-item--active primary--text': viewMode === 'grid' }"
                  @click="viewMode = 'grid'"
                >
                  <v-list-item-icon class="mr-3">
                    <font-awesome-icon :icon="['fas', 'grip']" />
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Grid View</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action v-if="viewMode === 'grid'">
                    <font-awesome-icon
                      :icon="['fas', 'check']"
                      class="primary--text"
                    />
                  </v-list-item-action>
                </v-list-item>
                <v-list-item
                  :class="{ 'v-list-item--active primary--text': viewMode === 'table' }"
                  @click="viewMode = 'table'"
                >
                  <v-list-item-icon class="mr-3">
                    <font-awesome-icon :icon="['fas', 'table']" />
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Table View</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action v-if="viewMode === 'table'">
                    <font-awesome-icon
                      :icon="['fas', 'check']"
                      class="primary--text"
                    />
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>

          <!-- Refresh Button -->
          <v-col
            cols="auto"
            class="py-1"
          >
            <v-btn
              outlined
              class="text-none"
              :loading="isRefreshing"
              :disabled="isRefreshing || loading"
              @click="refreshData"
            >
              <font-awesome-icon
                :icon="['fas', 'arrows-rotate']"
                size="sm"
                :class="{ 'mr-1': $vuetify.breakpoint.mdAndUp }"
              />
              <span class="d-none d-md-inline">Refresh</span>
            </v-btn>
          </v-col>

          <!-- Export Button -->
          <v-col
            cols="auto"
            class="py-1"
          >
            <v-menu
              offset-y
              nudge-bottom="6"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  outlined
                  class="text-none"
                  :disabled="!showExportBar"
                  v-bind="attrs"
                  v-on="on"
                >
                  <font-awesome-icon
                    :icon="['fas', 'file-export']"
                    size="sm"
                    :class="{ 'mr-1': $vuetify.breakpoint.mdAndUp }"
                  />
                  <span class="d-none d-md-inline">Export</span>
                </v-btn>
              </template>

              <v-list dense>
                <v-list-item
                  v-for="item in exportOptions"
                  :key="item.key"
                  :disabled="!item.active"
                  @click="item.callback"
                >
                  <v-list-item-title v-if="item.key !== 'xlsx'">
                    {{ item.text }}
                  </v-list-item-title>
                  <v-list-item-title v-else>
                    <export-xlsx
                      :item="item"
                      :show-export-bar="showExportBar"
                      :columns="exportColumns"
                      :groups="exportGroups"
                      :export-report-name="exportReportName"
                    />
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>

          <v-spacer class="d-none d-sm-flex" />
        </v-row>
      </div>
    </div>

    <!-- Jump to Group (contextual row) -->
    <jump-to-group
      :groups="exportGroups"
      class="ma-0 mb-1 ml-3"
      @scroll-to-group="scrollToGroup"
    />

    <!-- Quick Stats Banner (visible when filtering/searching) -->
    <v-row
      v-if="showQuickStats"
      class="ma-0 mb-3"
    >
      <v-col>
        <v-alert
          dense
          text
          color="primary"
          class="mb-0"
        >
          <div class="d-flex align-center flex-wrap">
            <div class="d-flex align-center mr-4 mb-1">
              <font-awesome-icon
                :icon="['fas', 'filter']"
                class="mr-2"
                size="sm"
              />
              <span class="font-weight-medium">
                Showing {{ filteredClients.length }} of {{ tabClients.length }} {{ currentTab.label.toLowerCase() }} client{{ tabClients.length === 1 ? '' : 's' }}
              </span>
            </div>
            <div
              v-if="searchText"
              class="d-flex align-center mb-1"
            >
              <font-awesome-icon
                :icon="['fas', 'magnifying-glass']"
                class="mr-2"
                size="sm"
              />
              <span>Search: "{{ searchText }}"</span>
            </div>
          </div>
        </v-alert>
      </v-col>
    </v-row>

    <!-- Loading State -->
    <v-row
      v-if="loading && clients.length === 0"
      class="mt-4"
    >
      <v-col class="text-center py-12">
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
        />
        <p class="mt-4 text-subtitle-1">
          Loading clients...
        </p>
      </v-col>
    </v-row>

    <!-- Error State -->
    <v-row
      v-else-if="error"
      class="mt-4"
    >
      <v-col>
        <v-alert
          type="error"
          prominent
        >
          <v-row align="center">
            <v-col class="grow">
              {{ error }}
            </v-col>
            <v-col class="shrink">
              <v-btn
                @click="fetchClientsData"
              >
                Retry
              </v-btn>
            </v-col>
          </v-row>
        </v-alert>
      </v-col>
    </v-row>

    <!-- Empty State -->
    <v-row
      v-else-if="filteredClients.length === 0 && viewMode !== 'table'"
      class="mt-4"
    >
      <v-col>
        <v-card
          class="pa-8 text-center"
          elevation="2"
        >
          <font-awesome-icon
            :icon="['fas', 'inbox']"
            size="4x"
            class="mb-4"
            style="color: #9e9e9e;"
          />
          <h3 class="text-h6 mb-2">
            No clients found
          </h3>
          <p class="text-body-2 text--secondary">
            {{ hasActiveFilters || searchText ? 'Try adjusting your filters or search terms' : 'No clients available in this view' }}
          </p>
          <v-btn
            v-if="hasActiveFilters || searchText"
            color="primary"
            outlined
            class="mt-4"
            @click="clearAllFilters"
          >
            Clear Filters
          </v-btn>
        </v-card>
      </v-col>
    </v-row>

    <!-- Client Cards Grid View - Grouped -->
    <template v-if="viewMode === 'grid' && localGroupByProperties.length > 0">
      <div
        v-for="(groupClients, groupKey) in groupedClients"
        :id="`group-${groupKey}`"
        :key="groupKey"
        class="mt-4"
      >
        <v-row class="mb-2">
          <v-col>
            <v-chip
              color="primary"
              label
              outlined
            >
              <font-awesome-icon
                :icon="['fas', 'layer-group']"
                size="sm"
                class="mr-2"
              />
              {{ groupKey }}
              <v-chip
                x-small
                class="ml-2"
              >
                {{ groupClients.length }}
              </v-chip>
            </v-chip>
          </v-col>
        </v-row>
        <v-row>
          <v-col
            v-for="client in groupClients"
            :key="client.billingCode"
            cols="12"
            sm="6"
            md="4"
            lg="3"
          >
            <client-card
              :client="client"
              :config="configs[client.billingCode]"
              :publisher="publishers[client.billingCode]"
              :loading-config="isLoadingConfig(client.billingCode)"
              :loading-publisher="isLoadingPublisher(client.billingCode)"
              :is-admin="isUserInternalAdmin"
              @view-profile="navigateToProfile"
              @edit="editClient"
              @delete="deleteClient"
              @config-fixed="handleConfigFixed"
            />
          </v-col>
        </v-row>
      </div>
    </template>

    <!-- Client Cards Grid View - No Grouping -->
    <v-row
      v-else-if="viewMode === 'grid'"
      class="mt-4"
    >
      <v-col
        v-for="client in filteredClients"
        :key="client.billingCode"
        cols="12"
        sm="6"
        md="4"
        lg="3"
      >
        <client-card
          :client="client"
          :config="configs[client.billingCode]"
          :publisher="publishers[client.billingCode]"
          :loading-config="isLoadingConfig(client.billingCode)"
          :loading-publisher="isLoadingPublisher(client.billingCode)"
          :is-admin="isUserInternalAdmin"
          @view-profile="navigateToProfile"
          @edit="editClient"
          @delete="deleteClient"
          @config-fixed="handleConfigFixed"
        />
      </v-col>
    </v-row>

    <!-- Clients Table View -->
    <v-row
      v-else-if="viewMode === 'table'"
      class="mt-4"
    >
      <v-col>
        <data-table
          :data-groups="currentDataGroups"
          :hide-toolbar-buttons="true"
          report-key="organization"
        >
          <!-- Client Name Column -->
          <template v-slot:item.clientName="{ item }">
            <div class="col align-center">
              <font-awesome-icon
                v-if="hasIssues(item)"
                :icon="['fas', 'exclamation-triangle']"
                class="error--text mr-2"
                size="sm"
              />
              <font-awesome-icon
                v-else
                :icon="['fas', 'check']"
                size="sm"
                class="mr-2"
              />
              <span class="font-weight-medium">{{ item.clientName }}</span>
            </div>
          </template>

          <!-- Billing Code Column -->
          <template v-slot:item.billingCode="{ item }">
            <v-col>
              <span>{{ item.billingCode }}</span>
            </v-col>
          </template>

          <!-- Active Column -->
          <template v-slot:item.isActive="{ item }">
            <v-col
              class="align-center"
            >
              <font-awesome-icon
                :icon="['fas', item.isActive ? 'circle-check' : 'circle-xmark']"
                :class="getClientStatusColor(item)"
                size="lg"
              />
            </v-col>
          </template>

          <!-- Timesheet Column -->
          <template v-slot:item.isTimesheetEnabled="{ item }">
            <v-col
              class="align-center"
            >
              <font-awesome-icon
                :icon="['fas', item.isTimesheetEnabled ? 'circle-check' : 'circle-minus']"
                :style="{ color: item.isTimesheetEnabled ? '#4caf50' : '#bdbdbd' }"
                size="lg"
              />
            </v-col>
          </template>

          <!-- Config Column -->
          <template v-slot:item.config="{ item }">
            <v-col
              class="align-center"
            >
              <v-skeleton-loader
                v-if="isLoadingConfig(item.billingCode)"
                type="text"
                max-width="80"
              />
              <font-awesome-icon
                v-else

                :icon="['fas', configs[item.billingCode] ? 'circle-check' : 'circle-xmark']"
                :style="{ color: configs[item.billingCode] ? '#4caf50' : '#f44336' }"
                size="lg"
              />
            </v-col>
          </template>

          <!-- Publisher Column -->
          <template v-slot:item.publisher="{ item }">
            <v-col
              class="align-center"
            >
              <v-skeleton-loader
                v-if="isLoadingPublisher(item.billingCode)"
                type="text"
                max-width="80"
              />
              <font-awesome-icon
                v-else
                :icon="['fas', publishers[item.billingCode] ? 'circle-check' : 'circle-xmark']"
                :style="{ color: publishers[item.billingCode] ? '#4caf50' : '#f44336' }"
                size="lg"
              />
            </v-col>
          </template>

          <!-- Actions Column -->
          <template v-slot:item.actions="{ item }">
            <v-btn
              icon
              color="primary"
              @click="navigateToProfile(item)"
            >
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <font-awesome-icon
                    :icon="['fas', 'eye']"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <span>View Profile</span>
              </v-tooltip>
            </v-btn>

            <v-btn
              v-if="isUserInternalAdmin"
              icon
              color="primary"
              @click="editClient(item)"
            >
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <font-awesome-icon
                    :icon="['fas', 'edit']"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <span>Edit</span>
              </v-tooltip>
            </v-btn>

            <v-btn
              v-if="isUserInternalAdmin"
              icon
              color="error"
              @click="deleteClient(item)"
            >
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <font-awesome-icon
                    :icon="['fas', 'trash']"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <span>Delete</span>
              </v-tooltip>
            </v-btn>
          </template>
        </data-table>
      </v-col>
    </v-row>

    <!-- Organization Modal (for Create/Edit/Delete) -->
    <org-modal @data-updated="fetchClientsData" />
  </v-col>
</template>

<script>
/**
 * Organization Component - Refactored Client Management View
 *
 * This component displays clients in a card-based tabbed layout.
 * Follows the Vue 3 migration pattern with component-level state management.
 *
 * Key Features:
 * - Tabbed interface (Active, All, Inactive, Issues, Timesheet-Enabled)
 * - Card-based layout with loading skeletons
 * - Component-level data management
 * - Direct API calls (no Vuex dependencies for data fetching)
 * - Client-side filtering and search
 * - Tab-specific summaries
 * - Progressive data loading (clients → configs → publishers)
 */

import { createNamespacedHelpers } from 'vuex'
import _ from 'underscore'

import dates from 'src/utils/dates'
import groupUtil from 'src/utils/group'
import api from '@/store/api.js'
import { firecollection } from '@/store/firebase.js'
import columns from '@/internal/store/utils/columns.js'
import tabQuerySync from '@/mixins/tabQuerySync.js'
import columnUtils from '@/tools/store/utils/columns.js'
import { organizationPath } from '@/utils/internalPaths'

const ExportXlsx = () => import('@/tools/components/export/ExportXlsx')
const ORGANIZATION_COLUMNS = columns.organizations
const DEFAULT_VISIBLE_ORGANIZATION_COLUMNS = ORGANIZATION_COLUMNS
  .filter((column) => column.visible !== false)
  .map((column) => column.key)

const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')
const { mapGetters: mapToolsGetters, mapState: mapToolsState } = createNamespacedHelpers('tools')

export default {
  name: 'ClientManagement',
  components: {
    ClientCard: () => import('../components/ClientCard.vue'),
    ClientFilterContent: () => import('../components/ClientFilterContent.vue'),
    ClientProfile: () => import('../components/Org.vue'),
    DataTable: () => import('@/tools/components/table/DataTable'),
    ExportXlsx,
    OrgModal: () => import('../components/OrgModal.vue'),
    ToolsToolbar: () => import('@/tools/components/filter/ToolsToolbar.vue'),
    JumpToGroup: () => import('@/tools/components/filter/JumpToGroup.vue'),
    SearchField: () => import('@/tools/components/filter/SearchField.vue'),
  },
  mixins: [tabQuerySync],
  data() {
    return {
      // Path utility for navigation
      organizationPath,
      // Tab query sync configuration
      tabQueryConfig: {
        queryParam: 'tab',
        validValues: ['active', 'all-clients', 'all-publishers', 'deleted', 'inactive', 'issues', 'timesheet'],
        defaultValue: 'active',
        additionalValidParams: ['search'],
        disabledWhen: () => {
          // Disable tab query sync on profile pages (when reportId is present)
          const { reportId } = this.$route?.params || {}
          return !!reportId && reportId !== 'dashboard'
        },
      },

      // Tab configuration
      tabs: [
        {
          id: 'active',
          label: 'Active Clients',
          icon: ['fas', 'check-circle'],
        },
        {
          id: 'all-clients',
          label: 'All Clients',
          icon: ['fas', 'building'],
        },
        {
          id: 'all-publishers',
          label: 'Publishers',
          icon: ['fas', 'network-wired'],
        },
        {
          id: 'inactive',
          label: 'Inactive',
          icon: ['fas', 'ban'],
        },
        {
          id: 'issues',
          label: 'Issues',
          icon: ['fas', 'exclamation-triangle'],
        },
        {
          id: 'timesheet',
          label: 'Time Enabled',
          icon: ['fas', 'clock'],
        },
      ],

      // Component-level state
      clients: [],
      publishers: {},
      configs: {},
      services: [],
      loading: false,
      isRefreshing: false,
      loadingConfigs: false,
      loadingPublishers: false,
      error: null,

      // View mode (persisted to localStorage)
      viewMode: 'grid', // 'grid' or 'table'

      // Search and filter state
      searchText: '',
      selectedStatusFilter: null,
      selectedTimesheetFilter: null,
      selectedConfigFilter: null,
      selectedPublisherFilter: null,

      // Advanced filters
      selectedDiscrepancyFilter: null,
      selectedPacingFilter: null,
      selectedYieldFilter: null,
      selectedAdxHealthFilter: null,
      selectedNetworkHealthFilter: null,

      // Grouping state
      localGroupByProperties: [],

      // Column visibility state (used for initial columns setup)
      visibleColumns: [...DEFAULT_VISIBLE_ORGANIZATION_COLUMNS],
    }
  },
  computed: {
    ...mapAuthGetters([
      'currentUser',
      'isInternalUser',
      'isUserInternalAdmin',
    ]),
    ...mapToolsGetters([
      'currentGroupByProperties',
      'reportKey',
      'tableColumns',
    ]),
    ...mapToolsState({
      selectedSearch: (state) => state.selectedSearch,
      sortingByKeys: (state) => state.sortingByKeys || [],
      sortingByDirections: (state) => state.sortingByDirections || [],
    }),

    /**
     * Active tab index - syncs with URL query parameter
     */
    activeTab: {
      get() {
        return this.activeTabIndex
      },
      set(index) {
        this.setActiveTab(index)
      },
    },

    reportId() {
      return this.selectedSearch && this.selectedSearch.reportId
    },

    /**
     * Determine if we should show the client profile view
     * Show profile when there's a reportId (billingCode) in the route
     */
    showClientProfile() {
      const { reportId } = this.$route.params
      return !!reportId && reportId !== 'dashboard'
    },

    currentTab() {
      return this.tabs[this.activeTab] || this.tabs[0]
    },

    /**
     * Get clients for the current tab
     */
    tabClients() {
      const { id } = this.currentTab

      switch (id) {
        case 'active':
          return this.clients.filter((client) => {
            const config = this.configs[client.billingCode] || {}
            const isClientDeleted = client.isDeleted === true
            const isClientActive = () => {
              const isConfigActive = () => config.access?.active !== false
              return client.isActive === true && isConfigActive()
            }

            return !isClientDeleted && isClientActive()
          })

        case 'all-publishers':
          return this.clients.filter((client) => {
            const publisher = this.publishers[client.billingCode]
            const isClientDeleted = client.isDeleted === true
            const isPublisherValid = () => !!publisher && typeof publisher === 'object' && publisher.isDeleted !== true

            return !isClientDeleted && isPublisherValid()
          })

        case 'inactive':
          // Inactive clients are those that are marked inactive on the client or config level.
          return this.clients.filter((client) => {
            const config = this.configs[client.billingCode] || {}
            const isClientDeleted = client.isDeleted === true
            const isClientActive = () => {
              const isConfigActive = () => config.access?.active !== false
              return client.isActive && isConfigActive()
            }

            return !isClientDeleted && !isClientActive()
          })

        case 'issues':
          return this.clients.filter((client) => {
            const isClientDeleted = client.isDeleted === true

            const hasIssues = () => {
              const config = this.configs[client.billingCode]
              const publisher = this.publishers[client.billingCode]

              const hasValidConfig = () => !config || (typeof config === 'object' && config.access && typeof config.access === 'object' && config.access.active === true)
              const hasValidPublisher = () => !publisher || (typeof publisher === 'object' && publisher.isDeleted === false)

              return !hasValidConfig() || !hasValidPublisher()
            }

            return !isClientDeleted && hasIssues()
          })

        case 'timesheet':
          return this.clients.filter((client) => {
            const isClientDeleted = client.isDeleted === true
            return !isClientDeleted && client.isTimesheetEnabled === true
          })

        case 'deleted':
          return this.clients.filter((client) => client.isDeleted === true)

        case 'all-clients':
        default:
          return this.clients.filter((client) => client.isDeleted === false)
      }
    },

    /**
     * Apply search and filters to tab clients
     */
    filteredClients() {
      let filtered = this.tabClients

      // Apply search
      if (this.searchText && typeof this.searchText === 'string') {
        const searchTerm = this.searchText.toLowerCase()
        filtered = filtered.filter((client) => {
          const nameMatch = client.clientName
            && typeof client.clientName === 'string'
            && client.clientName.toLowerCase().includes(searchTerm)
          const codeMatch = client.billingCode
            && typeof client.billingCode === 'string'
            && client.billingCode.toLowerCase().includes(searchTerm)
          return nameMatch || codeMatch
        })
      }

      // Apply status filter
      if (this.selectedStatusFilter !== null) {
        filtered = filtered.filter((client) => {
          const config = this.configs[client.billingCode] || {}
          const isActive = client.isActive && config.access?.active !== false
          return this.selectedStatusFilter === 'active' ? isActive : !isActive
        })
      }

      // Apply timesheet filter
      if (this.selectedTimesheetFilter !== null) {
        filtered = filtered.filter((client) => {
          const isEnabled = client.isTimesheetEnabled
          return this.selectedTimesheetFilter === 'enabled' ? isEnabled : !isEnabled
        })
      }

      // Apply config filter
      if (this.selectedConfigFilter !== null) {
        filtered = filtered.filter((client) => {
          const hasConfig = !!this.configs[client.billingCode]
          return this.selectedConfigFilter === 'has-config' ? hasConfig : !hasConfig
        })
      }

      // Apply publisher filter
      if (this.selectedPublisherFilter !== null) {
        filtered = filtered.filter((client) => {
          const hasPublisher = !!this.publishers[client.billingCode]
          return this.selectedPublisherFilter === 'has-publisher' ? hasPublisher : !hasPublisher
        })
      }

      // Apply advanced filters
      // Discrepancy filter
      if (this.selectedDiscrepancyFilter !== null) {
        filtered = filtered.filter((client) => {
          const publisher = this.publishers[client.billingCode]
          const hasDiscrepancy = publisher?.hasDiscrepancy || false
          return this.selectedDiscrepancyFilter === 'has-discrepancy' ? hasDiscrepancy : !hasDiscrepancy
        })
      }

      // Pacing filter
      if (this.selectedPacingFilter !== null) {
        filtered = filtered.filter((client) => {
          const publisher = this.publishers[client.billingCode]
          const hasPacing = publisher?.hasPacingReport || false
          return this.selectedPacingFilter === 'has-pacing' ? hasPacing : !hasPacing
        })
      }

      // Yield filter
      if (this.selectedYieldFilter !== null) {
        filtered = filtered.filter((client) => {
          const publisher = this.publishers[client.billingCode]
          const hasYield = publisher?.hasYieldPartnerReport || false
          return this.selectedYieldFilter === 'has-yield' ? hasYield : !hasYield
        })
      }

      // AdX Health filter
      if (this.selectedAdxHealthFilter !== null) {
        filtered = filtered.filter((client) => {
          const publisher = this.publishers[client.billingCode]
          const hasAdxHealth = publisher?.hasHealthAdxReport || false
          return this.selectedAdxHealthFilter === 'has-adx-health' ? hasAdxHealth : !hasAdxHealth
        })
      }

      // Network Health filter
      if (this.selectedNetworkHealthFilter !== null) {
        filtered = filtered.filter((client) => {
          const publisher = this.publishers[client.billingCode]
          const hasNetworkHealth = publisher?.hasHealthNetworkReport || false
          return this.selectedNetworkHealthFilter === 'has-network-health' ? hasNetworkHealth : !hasNetworkHealth
        })
      }

      // Apply sorting from tools store
      if (this.sortingByKeys && this.sortingByKeys.length > 0) {
        filtered = this.applySorting([...filtered])
      }

      return filtered
    },

    /**
     * Filter options
     */
    statusFilterOptions() {
      return [
        { text: 'Active', value: 'active' },
        { text: 'Inactive', value: 'inactive' },
      ]
    },

    timesheetFilterOptions() {
      return [
        { text: 'Enabled', value: 'enabled' },
        { text: 'Disabled', value: 'disabled' },
      ]
    },

    configFilterOptions() {
      return [
        { text: 'Has Configuration', value: 'has-config' },
        { text: 'Missing Configuration', value: 'missing-config' },
      ]
    },

    publisherFilterOptions() {
      return [
        { text: 'Has Publisher', value: 'has-publisher' },
        { text: 'Missing Publisher', value: 'missing-publisher' },
      ]
    },

    hasActiveFilters() {
      return !!(
        this.selectedStatusFilter
        || this.selectedTimesheetFilter
        || this.selectedConfigFilter
        || this.selectedPublisherFilter
        || this.selectedDiscrepancyFilter
        || this.selectedPacingFilter
        || this.selectedYieldFilter
        || this.selectedAdxHealthFilter
        || this.selectedNetworkHealthFilter
      )
    },

    showQuickStats() {
      // Show when actively filtering/searching
      return this.searchText && this.searchText.length > 0
    },

    activeFilterCount() {
      let count = 0
      if (this.selectedStatusFilter) count += 1
      if (this.selectedTimesheetFilter) count += 1
      if (this.selectedConfigFilter) count += 1
      if (this.selectedPublisherFilter) count += 1
      if (this.selectedDiscrepancyFilter) count += 1
      if (this.selectedPacingFilter) count += 1
      if (this.selectedYieldFilter) count += 1
      if (this.selectedAdxHealthFilter) count += 1
      if (this.selectedNetworkHealthFilter) count += 1
      return count
    },

    /**
     * Grouping options for clients
     */
    groupingOptions() {
      return [
        { text: 'Billing Code', value: 'billingCode' },
        { text: 'Status', value: 'isActive' },
        { text: 'Timesheet', value: 'isTimesheetEnabled' },
      ]
    },

    organizationColumns() {
      const activeVisibleColumns = this.visibleColumns?.length
        ? this.visibleColumns
        : DEFAULT_VISIBLE_ORGANIZATION_COLUMNS

      return ORGANIZATION_COLUMNS.map((column, index) => ({
        ...column,
        index,
        visible: activeVisibleColumns.includes(column.key),
      }))
    },

    /**
     * Available table columns for column picker
     */
    availableTableColumns() {
      return this.organizationColumns.map(({ columnName, key, required = false, visible = true }) => ({
        columnName,
        key,
        required,
        visible,
      }))
    },

    /**
     * Required columns that cannot be hidden
     */
    requiredColumns() {
      return this.availableTableColumns
        .filter((col) => col.required)
        .map((col) => col.key)
    },

    /**
     * Get visible column values from tableColumns store state
     */
    storeVisibleColumns() {
      const getFallbackColumns = () => (this.visibleColumns && this.visibleColumns.length > 0
        ? this.visibleColumns
        : DEFAULT_VISIBLE_ORGANIZATION_COLUMNS)

      if (!this.tableColumns || this.tableColumns.length === 0) {
        // Fallback to default visible columns if store not yet initialized
        return getFallbackColumns()
      }

      const storeColumns = this.tableColumns
        .filter((col) => col.visible !== false)
        .map((col) => col.key)

      // Guard against stale/invalid persisted columns causing an empty table.
      return storeColumns.length > 0 ? storeColumns : getFallbackColumns()
    },

    /**
     * Table headers for table view (respects column visibility)
     */
    tableHeaders() {
      const allHeaders = this.organizationColumns.map((column) => ({
        align: column.align || 'start',
        columnName: column.columnName,
        index: column.index,
        key: column.key,
        sortable: column.sortable !== false,
        value: column.key,
        width: column.width,
      }))

      // Filter headers based on visible columns from store
      const filteredHeaders = allHeaders.filter((header) => this.storeVisibleColumns.includes(header.key))

      // Fall back to all headers when persisted/store columns are stale.
      return filteredHeaders.length > 0 ? filteredHeaders : allHeaders
    },

    /**
     * Getter functions for group-by properties with boolean label transformations
     * Shared by groupedClients (card view) and currentDataGroups (table view)
     */
    groupGetters() {
      return this.localGroupByProperties.map((prop) => {
        if (prop === 'isActive') {
          return (item) => (item.isActive ? 'Active' : 'Inactive')
        } if (prop === 'isTimesheetEnabled') {
          return (item) => (item.isTimesheetEnabled ? 'Timesheet Enabled' : 'Timesheet Disabled')
        }
        return (item) => item[prop] || 'Unassigned'
      })
    },

    /**
     * Grouped clients based on localGroupByProperties
     * Uses groupUtil.byProperty() with custom getter for boolean label transformations
     */
    groupedClients() {
      if (this.localGroupByProperties.length === 0) {
        return {}
      }

      const groups = groupUtil.byProperty(this.filteredClients, this.groupGetters)

      // Convert array format to object format for template iteration
      const groupsObject = {}
      groups.forEach((group) => {
        if (group.isPartOfGroup && group.groupName) {
          groupsObject[group.groupName] = group.items
        }
      })

      return groupsObject
    },

    /**
     * Show export bar when there's data to export
     */
    showExportBar() {
      return this.filteredClients && this.filteredClients.length > 0
    },

    /**
     * Export options for the dropdown menu
     */
    exportOptions() {
      const { showExportBar } = this

      return [
        {
          active: showExportBar,
          key: 'csv',
          text: 'CSV',
          callback: this.exportAsCsv,
        },
        {
          active: showExportBar,
          key: 'xlsx',
          text: 'XLSX',
          callback: this.exportAsXlsx,
        },
      ]
    },

    /**
     * Export columns configuration for clients
     */
    exportColumns() {
      return [
        {
          key: 'clientName',
          columnName: 'Client Name',
          formatAs: 'text',
          columnValue: (item) => item.clientName || '',
        },
        {
          key: 'billingCode',
          columnName: 'Billing Code',
          formatAs: 'text',
          columnValue: (item) => item.billingCode || '',
        },
        {
          key: 'isActive',
          columnName: 'Status',
          formatAs: 'text',
          columnValue: (item) => (item.isActive ? 'Active' : 'Inactive'),
        },
        {
          key: 'isTimesheetEnabled',
          columnName: 'Timesheet',
          formatAs: 'text',
          columnValue: (item) => (item.isTimesheetEnabled ? 'Enabled' : 'Disabled'),
        },
        {
          key: 'hasConfig',
          columnName: 'Has Config',
          formatAs: 'text',
          columnValue: (item) => (this.configs[item.billingCode] ? 'Yes' : 'No'),
        },
        {
          key: 'hasPublisher',
          columnName: 'Has Publisher',
          formatAs: 'text',
          columnValue: (item) => (this.publishers[item.billingCode] ? 'Yes' : 'No'),
        },
      ]
    },

    /**
     * Export groups formatted for the tools/exportAs action
     */
    exportGroups() {
      if (this.localGroupByProperties.length > 0 && Object.keys(this.groupedClients).length > 0) {
        return Object.entries(this.groupedClients).map(([groupKey, items]) => ({
          key: groupKey,
          name: groupKey,
          groupName: groupKey,
          labels: [groupKey],
          items,
          rows: items,
        }))
      }

      // Default: single ungrouped list
      return [
        {
          key: 'all',
          name: 'All Clients',
          groupName: 'All Clients',
          labels: ['All Clients'],
          items: this.filteredClients,
          rows: this.filteredClients,
        },
      ]
    },

    /**
     * Export report name using current tab and date
     */
    exportReportName() {
      const tabLabel = this.currentTab?.label || 'Clients'
      const todayFormatted = dates.getFormattedDate(undefined, 'YYYY-MM-DD')

      return `clients_${tabLabel.replace(/\s+/g, '_').toLowerCase()}_${todayFormatted}`
    },

    /**
     * Format filtered clients into groups for DataTable
     * Uses standardized groupUtil for consistent grouping across all views
     */
    currentDataGroups() {
      const items = this.filteredClients || []

      // Use same getter functions as groupedClients for proper label transformations
      return groupUtil.byProperty(items, this.groupGetters)
    },

    /**
     * Get the client for the profile view based on route param
     * Used to populate selectedSearch when navigating via URL
     */
    profileClient() {
      const { reportId } = this.$route.params
      if (!reportId || reportId === 'dashboard') return null
      return this.clients.find((client) => client.billingCode === reportId)
    },
  },

  watch: {
    activeTab() {
      // Clear tab-specific filters when switching tabs
      this.clearAllFilters()
    },

    viewMode(newMode) {
      // Persist view mode to localStorage
      localStorage.setItem('clientsViewMode', newMode)
    },

    clients() {
      this.updateTabCounts()
    },

    configs: {
      handler() {
        this.updateTabCounts()
      },
      deep: true,
    },

    publishers: {
      handler() {
        this.updateTabCounts()
      },
      deep: true,
    },

    /**
     * Sync grouping from store to local state
     * This enables proper reactivity when grouping changes via ToolsDrawer
     */
    currentGroupByProperties: {
      immediate: true,
      handler(newGrouping) {
        if (newGrouping && Array.isArray(newGrouping)) {
          this.localGroupByProperties = [...newGrouping]
        }
      },
    },

    /**
     * Set selectedSearch when viewing a client profile via URL params
     * This ensures SearchBar displays the client name instead of "selection not found"
     */
    profileClient: {
      immediate: true,
      handler(client) {
        // Only set selectedSearch when viewing a profile
        if (!this.showClientProfile || !client) return

        const selectedSearch = {
          ...client,
          isSearchOption: true,
          route: 'internal',
          reportKey: 'organizations',
          reportId: client.billingCode,
          reportName: 'organizations',
          billingCode: client.billingCode,
          text: client.clientName || client.billingCode,
          name: client.clientName || client.billingCode,
          dataType: 'organization',
        }
        this.$store.commit('tools/selectedSearch', selectedSearch)
      },
    },
  },

  async mounted() {
    // Restore view mode from localStorage
    const savedViewMode = localStorage.getItem('clientsViewMode')
    if (savedViewMode && ['grid', 'table'].includes(savedViewMode)) {
      this.viewMode = savedViewMode
    }

    // Restore visible columns from localStorage
    const savedColumns = localStorage.getItem('clientsVisibleColumns')
    if (savedColumns) {
      try {
        const parsedColumns = JSON.parse(savedColumns)
        if (Array.isArray(parsedColumns)) {
          const validColumns = this.availableTableColumns.map((col) => col.key)
          const sanitizedColumns = parsedColumns.filter((col) => validColumns.includes(col))

          if (sanitizedColumns.length > 0) {
            this.visibleColumns = sanitizedColumns
          }
        }
      } catch (error) {
        // Invalid JSON, use defaults
        // eslint-disable-next-line no-console
        console.error('Error parsing saved columns:', error)
      }
    }

    // Initialize tools store options for ToolsDrawer
    this.initializeToolsStoreOptions()

    // Org modal actions depend on the internal namespace being registered.
    await this.ensureInternalStoreReady()

    await this.fetchClientsData()
  },

  methods: {
    /**
     * Normalized columns for Organization view.
     * Uses the shared internal columns utility as the single source of truth.
     */
    getSortableColumns() {
      return this.organizationColumns.map((column) => ({ ...column }))
    },

    /**
     * Initialize grouping and sorting options in tools store.
     * This enables ToolsDrawer to work with Organization view.
     */
    initializeToolsStoreOptions() {
      // Use the actual reportKey from tools store to ensure columns are found
      const reportKey = this.reportKey || 'organizations'

      // Define grouping options for Organization view
      const { groupingOptions } = this

      // Set grouping options in store
      this.$store.commit('tools/groupingOptions', { groupingOptions, reportKey })

      // Initialize empty grouping state if not already set
      if (!this.$store.state.tools.groupByProperties[reportKey]) {
        this.$store.commit('tools/grouping', { groupByProperties: [], reportKey })
      }

      // Set columns using the normalized internal column definitions.
      const sortableColumns = this.getSortableColumns()
      const existingColumns = this.$store.state.tools.columns[reportKey]

      if (!columnUtils.hasCompatibleColumnSchema(existingColumns, sortableColumns)) {
        const storedPrefs = columnUtils.loadColumnPreferences(reportKey)
        const mergedColumns = columnUtils.applyColumnPreferences(sortableColumns, storedPrefs)
        this.$store.commit('tools/columns', { [reportKey]: mergedColumns })
      }

      // Initialize default sorting by client name (ascending) if not set or if current sort key is invalid for this view
      const currentSortKeys = this.$store.state.tools.sortingByKeys || []
      const validKeys = sortableColumns.filter((c) => c.sortable).map((c) => c.key)
      const hasValidSortKey = currentSortKeys.length > 0 && currentSortKeys.every((key) => validKeys.includes(key))

      if (!hasValidSortKey) {
        this.$store.commit('tools/sortingByKeys', ['clientName'])
        this.$store.commit('tools/sortingByDirections', ['asc'])
      }
    },

    /**
     * Handle grouping changes from ToolsDrawer
     */
    onGroupingChange(groupByProperties) {
      this.localGroupByProperties = groupByProperties || []
    },

    /**
     * Handle sorting changes from ToolsDrawer
     * ToolsDrawer already commits to store, so we just need to trigger reactivity
     */
    onSortingChange(sorts) {
      // Sorting is handled by the store and applySorting computed
      // This handler allows views to react to sorting changes if needed
      this.$log.info('[Organization] Sorting changed:', sorts)
    },

    /**
     * Handle filter apply from ToolsDrawer
     * Filters are applied immediately via ClientFilterContent events,
     * so this just provides a handler for the Apply button.
     */
    onApplyFilters() {
      this.$log.info('[Organization] Filters applied')
    },

    /**
     * Handle filter reset from ToolsDrawer
     */
    onResetFilters() {
      this.clearAllFilters()
      this.$log.info('[Organization] Filters reset')
    },

    /**
     * Apply sorting to an array of clients based on sortingByKeys and sortingByDirections.
     * Uses the underscore extension methods for case-insensitive sorting.
     * @param {Array} items - Array of clients to sort
     * @returns {Array} Sorted array
     */
    applySorting(items) {
      const { sortingByKeys, sortingByDirections } = this

      if (!sortingByKeys || sortingByKeys.length === 0) {
        return items
      }

      // Apply sorting in reverse order (last key first) to maintain proper multi-level sort
      const sortedItems = sortingByKeys.reduceRight((sorted, key, index) => {
        const direction = sortingByDirections[index] || 'asc'
        const sortDsc = direction === 'desc'

        const valueGetter = (item) => {
          const value = item[key]
          // Handle different value types
          if (typeof value === 'boolean') {
            return value ? 1 : 0
          }
          if (typeof value === 'string') {
            return value.toLowerCase()
          }
          return value ?? ''
        }

        const sorter = sortDsc ? _.sortDscByCaseInsensitive : _.sortAscByCaseInsensitive
        return sorter(sorted, valueGetter)
      }, items)

      return sortedItems
    },

    /**
     * Export clients as CSV
     */
    exportAsCsv() {
      const exportData = {
        currentColumns: this.exportColumns,
        currentGroups: this.exportGroups,
        reportName: this.exportReportName,
        exportFileName: `${this.exportReportName}.csv`,
        exportType: 'csv',
      }

      this.$store.dispatch('tools/exportAs', exportData)
    },

    /**
     * Export clients as XLSX
     */
    exportAsXlsx() {
      const exportData = {
        currentColumns: this.exportColumns,
        currentGroups: this.exportGroups,
        reportName: this.exportReportName,
        exportFileName: `${this.exportReportName}.xlsx`,
        exportType: 'xlsx',
      }

      this.$store.dispatch('tools/exportAs', exportData)
    },

    /**
     * Scroll to a specific group in the grid view
     */
    scrollToGroup(groupKey) {
      this.$nextTick(() => {
        const element = document.getElementById(`group-${groupKey}`)
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          })
        }
      })
    },

    /**
     * Check if config is loading for a specific client
     */
    isLoadingConfig(billingCode) {
      return this.loadingConfigs && !this.configs[billingCode]
    },

    /**
     * Check if publisher is loading for a specific client
     */
    isLoadingPublisher(billingCode) {
      return this.loadingPublishers && !this.publishers[billingCode]
    },

    /**
     * Update tab counts based on current data
     */
    updateTabCounts() {
      // Safety check to ensure tabs exist
      if (!this.tabs || !Array.isArray(this.tabs)) {
        return
      }

      this.tabs = this.tabs.map((tab) => {
        let count = 0
        let chipColor = 'primary' // Default color (primary)

        switch (tab.id) {
          case 'active':
            count = this.clients.filter((client) => {
              const config = this.configs[client.billingCode] || {}
              return client.isActive && config.access?.active !== false
            }).length
            chipColor = 'primary'
            break

          case 'all-clients':
            count = this.clients.length
            chipColor = 'primary'
            break

          case 'all-publishers':
            count = this.clients.filter((client) => {
              const publisher = this.publishers[client.billingCode]
              return !!publisher && publisher.isDeleted !== true
            }).length
            chipColor = 'primary'
            break

          case 'inactive':
            count = this.clients.filter((client) => {
              const config = this.configs[client.billingCode] || {}
              return !client.isActive || config.access?.active === false
            }).length
            chipColor = 'gray'
            break

          case 'issues':
            count = this.clients.filter((client) => {
              const config = this.configs[client.billingCode]
              const publisher = this.publishers[client.billingCode]
              const hasNoConfig = !config
              const hasNoPublisher = !publisher
              const hasMismatch = client.clientId && publisher?.publisherId
                && client.clientId !== publisher.publisherId
              return hasNoConfig || hasNoPublisher || hasMismatch
            }).length

            chipColor = 'toasted'
            break

          case 'timesheet':
            count = this.clients.filter((client) => {
              const isClientDeleted = client.isDeleted === true
              return !isClientDeleted && client.isTimesheetEnabled === true
            }).length

            chipColor = 'primary'
            break

          default:
            count = 0
        }

        return { ...tab, count, chipColor }
      })
    },

    /**
     * Clear all active filters
     */
    clearAllFilters() {
      this.searchText = ''
      this.selectedStatusFilter = null
      this.selectedTimesheetFilter = null
      this.selectedConfigFilter = null
      this.selectedPublisherFilter = null
      this.selectedDiscrepancyFilter = null
      this.selectedPacingFilter = null
      this.selectedYieldFilter = null
      this.selectedAdxHealthFilter = null
      this.selectedNetworkHealthFilter = null
    },

    /**
     * Ensure the internal namespace is available before dispatching internal actions.
     * SearchBar can register stores lazily, so this guards action dispatch from racing.
     */
    async ensureInternalStoreReady() {
      if (this.$store.hasModule('internal')) {
        return true
      }

      try {
        await this.$store.dispatch('registerModule', 'internal')
      } catch (error) {
        if (!this.$store.hasModule('internal')) {
          // eslint-disable-next-line no-console
          console.error('[Organization] Failed to register internal store:', error)
          return false
        }
      }

      return true
    },

    /**
     * Navigate to client profile
     */
    async navigateToProfile(client) {
      const { billingCode } = client

      // Navigate to the client profile route using the path utility
      await this.$router.push(this.organizationPath.profile(billingCode))
    },

    /**
     * Add new client (Admin only)
     */
    async addClient() {
      const storeReady = await this.ensureInternalStoreReady()
      if (!storeReady) return

      this.$store.dispatch('internal/showFormOrganizationCreate')
    },

    /**
     * Edit client
     */
    async editClient(client) {
      const storeReady = await this.ensureInternalStoreReady()
      if (!storeReady) return

      this.$store.dispatch('internal/showFormOrganizationUpdate', client)
    },

    /**
     * Delete client
     */
    async deleteClient(client) {
      const storeReady = await this.ensureInternalStoreReady()
      if (!storeReady) return

      this.$store.dispatch('internal/showFormOrganizationDelete', client)
    },

    /**
     * Check if client has issues (for table view)
     */
    hasIssues(client) {
      const config = this.configs[client.billingCode]
      const publisher = this.publishers[client.billingCode]
      const hasNoConfig = !config
      const hasNoPublisher = !publisher
      const hasMismatch = client.clientId && publisher?.publisherId
        && client.clientId !== publisher.publisherId

      return hasNoConfig || hasNoPublisher || hasMismatch
    },

    /**
     * Get client status color using color psychology:
     * - Blue (primary): Active/engaged
     * - Gray (midgray): Inactive/neutral
     * - Red (reds): Config errors/critical
     */
    getClientStatusColor(client) {
      const config = this.configs[client.billingCode] || {}
      const { access } = config || {}

      const isConfigValid = typeof access === 'object' && access !== null
      const isConfigActive = isConfigValid && access.active !== false

      const isClientActive = client.isActive === true
      const isClientDeleted = client.isDeleted === true

      // Deleted clients - show neutral color
      if (isClientDeleted) {
        return 'reds--text'
      }

      // Config has issues - show alert color
      if (isClientActive && !isConfigActive) {
        return 'reds--text'
      }

      if (!isClientActive && isConfigActive) {
        return 'reds--text'
      }

      // Client inactive - show neutral color
      if (!isClientActive) {
        return 'midgray--text'
      }

      // Both active - show trust/stability color
      return 'greenery--text'
    },

    /**
     * Get organization configs from Firestore
     */
    async getOrgConfigs(catcher) {
      try {
        const configs = await firecollection.collection('organization', catcher)
        return configs || []
      } catch (error) {
        catcher(error)
        return []
      }
    },

    /**
     * Fetch all client data
     * Phase 1: Load clients immediately
     * Phase 2: Load configs from Firestore
     * Phase 3: Load publishers from API
     * Phase 4: Merge data
     */
    async fetchClientsData() {
      this.loading = true
      this.error = null

      const catcher = (error) => {
        this.error = error.message || 'Failed to load clients'

        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: `Error loading clients: ${this.error}`,
          status: 'error',
        }, { root: true })

        // eslint-disable-next-line no-console
        console.error('Error fetching clients:', error)
      }

      try {
        // Phase 1: Load clients immediately
        const clientsResponse = await api.getClients()
        const clients = clientsResponse?.content || []

        // Transform clients to add derived properties (matching organization.js mapClientAndPublisher logic)
        const transformedClients = clients.map((client) => {
          // Set isTimesheetEnabled based on organization.js line 152 logic
          const isTimesheetEnabled = !!client.billingCode && !client.isDeleted
          // @todo Rename hasTimesheetReport to isTimesheetEnabledLabel for clarity
          const hasTimesheetReport = `Time ${isTimesheetEnabled ? 'Enabled' : 'Disabled'}`

          return {
            ...client,
            isTimesheetEnabled,
            hasTimesheetReport,
          }
        })

        this.clients = transformedClients
        this.loading = false

        // Show initial success message
        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: `Loaded ${clients.length} clients`,
          status: 'success',
        }, { root: true })

        // Phase 2: Load configs in background
        this.loadingConfigs = true
        const configsArray = await this.getOrgConfigs(catcher)

        // Convert configs array to lookup object by billingCode (docId)
        const configsMap = {}

        configsArray.forEach((config) => {
          if (config.docId) {
            configsMap[config.docId] = config
          }
        })

        this.configs = configsMap
        this.loadingConfigs = false

        // Phase 3: Load publishers with teams in background
        this.loadingPublishers = true

        // Use auth store to get publishers with teams already loaded
        const { isInternalUser } = this
        const currentUser = this.$store.getters['auth/currentUser']

        const publishersResponse = await this.$store.dispatch('auth/fetchPublishersByRole', {
          isInternalUser,
          user: currentUser,
        })

        const publishers = publishersResponse?.content || []

        // Convert publishers array to lookup object by billingCode
        const publishersMap = {}

        publishers.forEach((publisher) => {
          if (publisher.billingCode) {
            publishersMap[publisher.billingCode] = publisher
          }
        })

        this.publishers = publishersMap
        this.loadingPublishers = false

        // Phase 4: Load services
        const servicesResponse = await api.getServices()

        this.services = servicesResponse?.content || []

        // Update tab counts after all data is loaded
        this.updateTabCounts()
      } catch (error) {
        catcher(error)
      } finally {
        this.loading = false
        this.loadingConfigs = false
        this.loadingPublishers = false
      }
    },

    /**
     * Refresh all data (called by Refresh button)
     */
    async refreshData() {
      this.isRefreshing = true
      try {
        await this.fetchClientsData()
      } finally {
        this.isRefreshing = false
      }
    },

    /**
     * Check if a column is visible
     */
    isColumnVisible(columnValue) {
      return this.visibleColumns.includes(columnValue)
    },

    /**
     * Toggle column visibility
     */
    toggleColumn(columnValue) {
      const index = this.visibleColumns.indexOf(columnValue)
      if (index > -1) {
        this.visibleColumns.splice(index, 1)
      } else {
        this.visibleColumns.push(columnValue)
      }

      // Persist to localStorage
      localStorage.setItem('clientsVisibleColumns', JSON.stringify(this.visibleColumns))
    },

    /**
     * Reset columns to default
     */
    resetColumns() {
      this.visibleColumns = [...DEFAULT_VISIBLE_ORGANIZATION_COLUMNS]
      localStorage.removeItem('clientsVisibleColumns')
    },

    /**
     * Format group header based on grouping property
     */
    formatGroupHeader(groupBy, groupValue) {
      if (!groupBy) return groupValue

      switch (groupBy) {
        case 'isActive':
          return groupValue ? 'Active Clients' : 'Inactive Clients'

        case 'isTimesheetEnabled':
          return groupValue ? 'Timesheet Enabled' : 'Timesheet Disabled'

        case 'billingCode':
          return `Billing Code: ${groupValue}`

        case 'clientName':
          // Group by first letter of client name
          return groupValue ? `Clients: ${groupValue}` : 'Unknown'

        default:
          return groupValue
      }
    },

    /**
     * Calculate metrics for a group of clients
     */
    getGroupMetrics(items) {
      if (!items || items.length === 0) return []

      const metrics = []

      // Count clients with configs
      const withConfig = items.filter((client) => this.configs[client.billingCode]).length
      if (withConfig > 0) {
        metrics.push({
          key: 'config',
          icon: ['fas', 'cog'],
          color: 'success--text',
          value: withConfig,
          label: withConfig === 1 ? 'config' : 'configs',
        })
      }

      // Count clients with publishers
      const withPublisher = items.filter((client) => this.publishers[client.billingCode]).length
      if (withPublisher > 0) {
        metrics.push({
          key: 'publisher',
          icon: ['fas', 'network-wired'],
          color: 'info--text',
          value: withPublisher,
          label: withPublisher === 1 ? 'publisher' : 'publishers',
        })
      }

      // Count clients with timesheet enabled
      const withTimesheet = items.filter((client) => client.isTimesheetEnabled).length
      if (withTimesheet > 0) {
        metrics.push({
          key: 'timesheet',
          icon: ['fas', 'clock'],
          color: 'primary--text',
          value: withTimesheet,
          label: 'timesheet',
        })
      }

      // Count clients with issues
      const withIssues = items.filter((client) => this.hasIssues(client)).length
      if (withIssues > 0) {
        metrics.push({
          key: 'issues',
          icon: ['fas', 'exclamation-triangle'],
          color: 'error--text',
          value: withIssues,
          label: withIssues === 1 ? 'issue' : 'issues',
        })
      }

      return metrics
    },

    /**
     * Handle config-fixed event from ClientCard
     * Refresh the config for the specific billing code
     */
    async handleConfigFixed(billingCode) {
      if (!billingCode) return

      const catcher = (error) => {
        // eslint-disable-next-line no-console
        console.error('Error refreshing config:', error)
      }

      try {
        // Reload the specific config from Firestore
        const config = await firecollection.get('organization', billingCode, catcher)

        if (config) {
          // Update the configs lookup
          this.$set(this.configs, billingCode, config)
        }
      } catch (error) {
        catcher(error)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Header styles (matching Time view pattern)
.clients-view-header-compact {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden; // Ensures children respect border-radius

  .header-toolbar {
    min-height: 56px !important;
    max-height: 56px !important;
    background: white !important;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  }

  .header-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: rgba(var(--v-primary-base), 0.08);
  }

  .clients-view-tabs-inline {
    ::v-deep .v-tabs-bar {
      height: 56px !important;
      background-color: white !important;
    }

    ::v-deep .v-tab {
      font-size: 0.875rem;
      font-weight: 500;
      letter-spacing: 0.25px;
      text-transform: none;
      min-width: 120px;
      padding: 0 16px;
      height: 56px;

      &:hover {
        background-color: rgba(var(--v-primary-base), 0.04);
      }
    }

    ::v-deep .v-tab--active {
      color: var(--v-primary-base);
    }

    ::v-deep .v-tabs-slider {
      height: 3px !important;
    }
  }

  .tab-compact {
    padding: 0 16px;
  }

  .add-client-btn-compact {
    text-transform: none;
    letter-spacing: normal;
    font-weight: 500;
  }

  // Tab count chips styling
  .tab-count-chip {
    background-color: #e0e0e0 !important;
    border-color: #bdbdbd !important;
    color: #616161 !important;
    font-weight: 600;
  }
}

// Controls bar styles (matching Time view)
.controls-bar {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);

  .search-field-compact {
    ::v-deep .v-input__control {
      min-height: 40px;
    }

    ::v-deep .v-input__slot {
      background-color: white !important;
    }

    .search-icon {
      color: rgba(0, 0, 0, 0.54);
    }
  }

  .filter-btn-compact {
    text-transform: none;
    letter-spacing: normal;
    height: 40px;

    &.filter-active {
      background-color: rgba(var(--v-primary-base), 0.08) !important;
      border-color: var(--v-primary-base) !important;
      border-width: 2px !important;
    }
  }
}

// Filter dialog styles
.filter-section {
  .v-expansion-panel {
    background: transparent !important;

    ::v-deep .v-expansion-panel-header {
      min-height: 48px;
      padding: 12px 0;
    }

    ::v-deep .v-expansion-panel-content__wrap {
      padding: 12px 0;
    }
  }
}

// Table view styles
.clients-table {
  &.striped-table {
    ::v-deep tbody tr:nth-of-type(odd) {
      background-color: rgba(0, 0, 0, 0.02);
    }

    ::v-deep tbody tr:hover {
      background-color: rgba(var(--v-primary-base), 0.04) !important;
    }
  }

  ::v-deep .v-data-table-header th {
    font-weight: 600 !important;
    font-size: 0.875rem !important;
    color: rgba(0, 0, 0, 0.87) !important;
  }

  ::v-deep td {
    font-size: 0.875rem;
  }

  // Group header styling
  ::v-deep .group-header-cell {
    background-color: #f5f5f5 !important;
    font-weight: 500;
    padding: 12px 16px !important;
    border-bottom: 2px solid rgba(0, 0, 0, 0.12) !important;

    &:hover {
      background-color: #eeeeee !important;
    }
  }
}

// Cursor pointer for clickable group headers
.cursor-pointer {
  cursor: pointer;
}

// Chevron rotation for group expand/collapse
.rotate-minus-90 {
  transform: rotate(-90deg);
  transition: transform 0.2s ease;
}

// Column picker row styles
.column-picker-row {
  border-radius: 4px;
  padding: 4px 8px;
  margin-left: -8px !important;
  margin-right: -8px !important;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &.column-required {
    cursor: default;
    opacity: 0.6;
  }

  &:hover:not(.column-required) {
    background-color: rgba(var(--v-primary-base), 0.04);
  }
}

// Dark mode support
.theme--dark {
  .clients-view-header-compact {
    background: #2d2d2d;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);

    .header-toolbar {
      background: #2d2d2d !important;
      border-bottom-color: rgba(255, 255, 255, 0.12);
    }

    .header-icon-wrapper {
      background: rgba(var(--v-primary-base), 0.15);
    }

    .clients-view-tabs-inline {
      ::v-deep .v-tabs-bar {
        background-color: #2d2d2d !important;
      }
    }

    .tab-count-chip {
      background-color: #424242 !important;
      border-color: #616161 !important;
      color: #e0e0e0 !important;
    }
  }

  .controls-bar {
    background: rgba(255, 255, 255, 0.05);
    border-bottom-color: rgba(255, 255, 255, 0.12);

    .search-field-compact {
      ::v-deep .v-input__slot {
        background-color: rgba(255, 255, 255, 0.05) !important;
      }

      .search-icon {
        color: rgba(255, 255, 255, 0.7);
      }
    }

    .filter-btn-compact.filter-active {
      background-color: rgba(var(--v-primary-base), 0.15) !important;
    }
  }

  .clients-table {
    &.striped-table {
      ::v-deep tbody tr:nth-of-type(odd) {
        background-color: rgba(255, 255, 255, 0.02);
      }

      ::v-deep tbody tr:hover {
        background-color: rgba(var(--v-primary-base), 0.08) !important;
      }
    }

    ::v-deep .v-data-table-header th {
      color: rgba(255, 255, 255, 0.87) !important;
    }

    // Group header dark mode
    ::v-deep .group-header-cell {
      background-color: #2d2d2d !important;
      border-bottom-color: rgba(255, 255, 255, 0.12) !important;

      &:hover {
        background-color: #3d3d3d !important;
      }
    }
  }
}
</style>
